import { useEffect, useMemo, useState } from 'react';
import useEffectiveApplicationGroupId from 'src/hooks/useEffectiveApplicationGroupId';
import useEffectiveApplicationId from 'src/hooks/useEffectiveApplicationId';
import { capitalize } from 'switch-fe-shared/utils/formatting';
import { AggregateP1SplitGroupBy } from 'switch-shared/modules/crud/utility-data/utility-data.dto';
import {
  UtilityType,
  convertToAndFromBase,
} from 'switch-shared/modules/crud/utility/utility.enums';

import { useDashboard } from '../../../../context/dashboardContext';
import { useGetAggregateP1SplitQuery } from '../../../../services/switch-rest-api/utility-data.service';
import { getUtilityUnits } from '../../../../utils/formatters';
import {
  isAllSelection,
  isAllUtilitySelection,
  toConcreteUtilityType,
} from '../../../../utils/selection';
import MetricCard from './MetricCard';

interface TrendData {
  value: number;
  percentageChange: number;
  isIncreasing: boolean;
}

interface BreakdownItem {
  label: string;
  value?: number;
  unit: string;
  trend?: TrendData;
}

interface SubMeterMetricWidgetProps {
  hideTrends?: boolean;
}

const SubMeterMetricWidget = ({
  hideTrends = false,
}: SubMeterMetricWidgetProps) => {
  const { utility, dateRange, bucketOptions, zone } = useDashboard();

  const appGroupId = useEffectiveApplicationGroupId();
  const appId = useEffectiveApplicationId();

  const unit = useMemo(() => {
    return getUtilityUnits(utility, true);
  }, [utility]);

  const [subMeter, setSubMeter] = useState<number | undefined>(undefined);
  const [trend, setTrend] = useState<TrendData | undefined>(undefined);
  const [breakdown, setBreakdown] = useState<BreakdownItem[] | undefined>(
    undefined
  );
  const groupBy = isAllUtilitySelection(utility)
    ? AggregateP1SplitGroupBy.Application
    : AggregateP1SplitGroupBy.Bucket;

  // Fetch data based on current selection
  const {
    data: utilityData,
    isLoading,
    isFetching,
  } = useGetAggregateP1SplitQuery(
    {
      startTimeISO: dateRange.start.toISOString(),
      endTimeISO: dateRange.end.toISOString(),
      applicationGroupId: appGroupId,
      applicationId: isAllSelection(appId) ? undefined : appId,
      utilityType: toConcreteUtilityType(utility),
      bucketSize: bucketOptions.size,
      bucketUnit: bucketOptions.unit,
      groupBy,
      zoneId: zone?._id,
      includePriorPeriod: !hideTrends,
    },
    {
      pollingInterval: 300_000,
      skip: !appGroupId && isAllSelection(appId),
    }
  );

  useEffect(() => {
    if (!utilityData) return;

    if (isAllUtilitySelection(utility)) {
      const utilityTypes = [
        UtilityType.ELECTRICITY,
        UtilityType.WATER,
        UtilityType.GAS,
      ];
      const nextBreakdown: BreakdownItem[] = [];

      utilityTypes.forEach((utilityType) => {
        const subMeterKey = `${utilityType}DeltaP1SubMeter`;
        const utilityTypeCap =
          utilityType.charAt(0).toUpperCase() + utilityType.slice(1);
        const priorSubMeterKey = `prior${utilityTypeCap}DeltaP1SubMeter`;

        let totalSubMeter = 0;
        let priorTotalSubMeter = 0;
        let hasData = false;
        let hasPriorData = false;

        utilityData.buckets?.forEach((bucket: any) => {
          if (bucket[subMeterKey] !== undefined) {
            hasData = true;
            totalSubMeter += bucket[subMeterKey] || 0;
          }

          if (bucket[priorSubMeterKey] !== undefined) {
            hasPriorData = true;
            priorTotalSubMeter += bucket[priorSubMeterKey] || 0;
          }
        });

        if (!hasData) return;

        const currentValue =
          convertToAndFromBase(totalSubMeter, utilityType as any, true) || 0;
        const priorValue = hasPriorData
          ? convertToAndFromBase(
              priorTotalSubMeter,
              utilityType as any,
              true
            ) || 0
          : undefined;

        nextBreakdown.push({
          label: capitalize(utilityType),
          value: currentValue,
          unit: getUtilityUnits(utilityType as any, true),
          trend:
            priorValue !== undefined && priorValue > 0
              ? {
                  value: currentValue,
                  percentageChange:
                    ((currentValue - priorValue) / priorValue) * 100,
                  isIncreasing: currentValue > priorValue,
                }
              : undefined,
        });
      });

      setBreakdown(nextBreakdown);
      setSubMeter(undefined);
      setTrend(undefined);
      return;
    }

    const totalSubMeter = utilityData?.totalDeltaP1SubMeter;
    const priorSubMeter = utilityData?.priorTotalDeltaP1SubMeter;

    const currentValue =
      totalSubMeter != null
        ? convertToAndFromBase(totalSubMeter, utility, true)
        : 0;

    setSubMeter(currentValue);

    if (priorSubMeter !== undefined) {
      const priorValue =
        priorSubMeter != null
          ? convertToAndFromBase(priorSubMeter, utility, true)
          : 0;

      if (priorValue > 0) {
        const percentageChange =
          ((currentValue - priorValue) / priorValue) * 100;
        setTrend({
          value: currentValue,
          percentageChange,
          isIncreasing: percentageChange > 0,
        });
      }
    }

    setBreakdown(undefined);
  }, [utilityData, utility]);

  return (
    <MetricCard
      title='Total Sub-Meter'
      value={subMeter}
      unit={unit}
      trend={trend}
      breakdown={breakdown}
      isLoading={isLoading || isFetching}
      hideTrends={hideTrends}
    />
  );
};

export default SubMeterMetricWidget;
